import type {
  IntegrationConnectionStatus,
  ScannerConnectionRow,
} from "./integrations.type"

/** Values returned in `connections[].scanner` (lowercase). Extend as more integrations ship. */
export const SCANNER_INTEGRATION_IDS = {
  EASYRX: "easyrx",
} as const

/** Union of all valid scanner integration ID strings derived from SCANNER_INTEGRATION_IDS. */
export type ScannerIntegrationId =
  (typeof SCANNER_INTEGRATION_IDS)[keyof typeof SCANNER_INTEGRATION_IDS]

/** All scanners to request in `Scanner` header for org-wide connection-status (comma-separated). */
export const ALL_SCANNER_INTEGRATION_IDS = [
  SCANNER_INTEGRATION_IDS.EASYRX,
] as const

/**
 * Scanners that use the shared Integrations OAuth popup + `/integrations/callback` protocol.
 * Add new ids when a scanner ships the same flow — minimal config.
 */
export const SCANNER_INTEGRATION_OAUTH_IDS: readonly string[] = [
  SCANNER_INTEGRATION_IDS.EASYRX,
]

/** Values for `RefreshPatientsResponse.reason` from POST …/patients/refresh */
export const SCANNER_REFRESH_REASON = {
  ALREADY_UP_TO_DATE: "already_up_to_date",
  CACHE_EXPIRED_FULL_RELOAD: "cache_expired_full_reload",
  DELTA_REFRESH: "delta_refresh",
} as const

/** Scanner list sync states from backend payloads (`sync_status`). */
export const SCANNER_SYNC_STATUS = {
  COMPLETED: "completed",
  IN_PROGRESS: "in_progress",
  FAILED: "failed",
  IDLE: "idle",
} as const

export function isRegisteredScannerOAuthId(id: string): boolean {
  return SCANNER_INTEGRATION_OAUTH_IDS.some((x) => isSameScannerId(x, id))
}

/**
 * `Scanner` request header: comma-separated names (trimmed). Accepts one id, many, or a pre-joined string.
 */
export function formatScannerHeaderValue(
  scanners: string | readonly string[],
): string {
  const parts = Array.isArray(scanners)
    ? scanners.map((s) => s.trim()).filter(Boolean)
    : String(scanners)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
  return parts.join(",")
}

export function isSameScannerId(
  a: string | undefined,
  b: string | undefined,
): boolean {
  if (a == null || b == null) return false
  return a.toLowerCase() === b.toLowerCase()
}

/** Whether the given scanner row is connected (`true` only if explicitly connected). */
export function getScannerConnected(
  connections: ScannerConnectionRow[] | undefined,
  scannerName: string,
): boolean {
  if (!connections?.length) return false
  const row = connections.find((c) => isSameScannerId(c.scanner, scannerName))
  return row?.connected === true
}

/** Map GET …/connection-status `result.connections` into Redux `connectionStatus`. */
export function buildIntegrationConnectionStatus(
  connections: ScannerConnectionRow[],
): IntegrationConnectionStatus {
  return Object.values(SCANNER_INTEGRATION_IDS).reduce(
    (acc, key) => ({ ...acc, [key]: getScannerConnected(connections, key) }),
    {} as IntegrationConnectionStatus,
  )
}
